/**
 * Phase 2D · /billing/success — polls the checkout status endpoint.
 * Per playbook: poll every 2s, max 5 attempts. States: pending/paid/expired/failed.
 *
 * Copy pass (Jun 20): term-based access wording.
 */
import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, AlertTriangle } from "lucide-react";

import { authService } from "@/services/authService";

const MAX_ATTEMPTS = 5;

const humanLabel = (label) => (label || "").replace(/\s*·\s*/g, " ");
const formatExpiry = (iso) => {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return "";
        return d.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
    } catch { return ""; }
};

export default function Success() {
    const [search] = useSearchParams();
    const sessionId = search.get("session_id");
    const navigate = useNavigate();
    const attemptsRef = useRef(0);
    const [state, setState] = useState("pending");   // pending | paid | expired | failed | error
    const [detail, setDetail] = useState(null);
    const [me, setMe] = useState(null);
    const [packages, setPackages] = useState([]);

    useEffect(() => {
        if (!sessionId) { setState("error"); return; }
        let cancelled = false;
        const poll = async () => {
            if (cancelled) return;
            attemptsRef.current += 1;
            try {
                const r = await authService.http.get(`/billing/checkout/status/${sessionId}`);
                if (cancelled) return;
                setDetail(r.data);
                const ps = r.data.payment_status;
                if (ps === "paid") {
                    setState("paid");
                    // Fetch /billing/me + /billing/packages to resolve plan label + expiry
                    try {
                        const [meRes, pkgRes] = await Promise.all([
                            authService.http.get("/billing/me"),
                            authService.http.get("/billing/packages"),
                        ]);
                        if (!cancelled) {
                            setMe(meRes.data);
                            setPackages(pkgRes.data.packages || []);
                        }
                    } catch { /* not fatal */ }
                    return;
                }
                if (ps === "expired") { setState("expired"); return; }
                if (ps === "failed") { setState("failed"); return; }
            } catch (e) {
                if (e?.response?.status === 403) { setState("error"); return; }
            }
            if (attemptsRef.current >= MAX_ATTEMPTS) { setState("expired"); return; }
            setTimeout(poll, 2000);
        };
        poll();
        return () => { cancelled = true; };
    }, [sessionId]);

    const planLabel = humanLabel(
        packages.find((p) => p.id === (detail?.package_id || me?.current_package_id))?.label
    );
    const expiry = formatExpiry(me?.subscription_until);

    return (
        <div data-testid="billing-success-page" className="min-h-screen bg-[#f6f7f6] text-[#0e1416] flex items-center justify-center p-6">
            <div className="bg-[#ffffff] border border-[#d8dcdd] p-8 max-w-[560px] w-full text-center">
                {state === "pending" && (
                    <>
                        <Loader2 className="animate-spin text-[#01696f] mx-auto" size={32} strokeWidth={1.5} />
                        <h2 className="mt-4 text-[22px] font-medium">Payment is being processed…</h2>
                        <p className="tc-mono text-[11px] text-[#5a6669] mt-2">Checking Stripe · attempt {attemptsRef.current}/{MAX_ATTEMPTS}</p>
                    </>
                )}
                {state === "paid" && (
                    <>
                        <CheckCircle2 className="text-[#01696f] mx-auto" size={40} strokeWidth={1.5} />
                        <h2 className="mt-4 text-[22px] font-medium" data-testid="billing-success-paid">Payment received</h2>
                        {(planLabel && expiry) ? (
                            <p data-testid="billing-success-term" className="tc-mono text-[12px] text-[#0e1416] mt-3 leading-[1.6]">
                                You&apos;re on <span className="text-[#01696f]">{planLabel}</span>. Your access is active until {expiry}.
                            </p>
                        ) : (
                            <p className="tc-mono text-[11.5px] text-[#5a6669] mt-2">
                                Package <span className="text-[#0e1416]">{detail?.package_id}</span> credited to your account.
                            </p>
                        )}
                        <button
                            onClick={() => navigate("/app")}
                            data-testid="billing-success-cta-app"
                            className="mt-6 tc-mono text-[11.5px] px-4 py-2 bg-[#01696f] hover:bg-[#015a60] text-[#ffffff]"
                        >
                            Go to app →
                        </button>
                    </>
                )}
                {(state === "expired" || state === "failed" || state === "error") && (
                    <>
                        <AlertTriangle className="text-[#b3741f] mx-auto" size={36} strokeWidth={1.5} />
                        <h2 className="mt-4 text-[22px] font-medium">
                            {state === "expired" ? "Checkout session expired" : state === "failed" ? "Payment failed" : "Session not found"}
                        </h2>
                        <p className="tc-mono text-[11.5px] text-[#5a6669] mt-2">
                            {state === "expired" ? "Stripe didn't return a paid status within the poll window. Try again." :
                             state === "failed"  ? "The payment was declined or cancelled." :
                                                   "We couldn't read this checkout session for your account."}
                        </p>
                        <Link
                            to="/billing"
                            data-testid="billing-success-cta-retry"
                            className="mt-6 inline-block tc-mono text-[11.5px] px-4 py-2 border border-[#01696f] text-[#01696f] hover:bg-[#01696f] hover:text-[#ffffff]"
                        >
                            Back to billing
                        </Link>
                    </>
                )}
            </div>
        </div>
    );
}
